"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Terms() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const domains = [{
    number: "01",
    label: "SECURITY PROTOCOLS",
    title: "Advanced encryption for total digital safety.",
    description: "Our robust security architecture ensures your data remains private, protected by industry-leading protocols designed for the modern web.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/security_encryption_shield.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/security_encryption_shield.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/security_encryption_shield.webp-wvc-sizes",
    alt: "Digital shield representing advanced encryption security",
    aspect: "aspect-[4/5]",
    to: "/VpnGenixPage"
  }, {
    number: "02",
    label: "PRIVACY STANDARDS",
    title: "Uncompromising anonymity for every user.",
    description: "We adhere to strict no-logs policies, ensuring your online footprint is invisible and your browsing habits remain strictly yours.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/privacy_anonymity_network.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/privacy_anonymity_network.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/privacy_anonymity_network.webp-wvc-sizes",
    alt: "Abstract network visualization representing online anonymity",
    aspect: "aspect-[5/4]",
    to: "/VpnGenixPage"
  }];
  return <section data-section-id="3035" id="termsandconditionsprotocol" className="relative bg-background text-foreground py-24 md:py-32 lg:py-44 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-y-8 mb-20 md:mb-28 lg:mb-32 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-start-2 lg:col-span-10 flex items-center gap-3 mb-4">
            <span className="inline-block w-2 h-2 rounded-lg bg-primary" />
            <span className="font-default text-xs tracking-[0.25em] uppercase text-muted-foreground">
              Our Standards
            </span>
          </div>
          <h2 className="lg:col-start-2 lg:col-span-8 font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
            Two pillars. One{" "}
            <em className="italic text-primary font-medium">commitment</em> to
            privacy.
          </h2>
        </div>

        {}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-y-16 lg:gap-y-0 lg:gap-x-8">
          {}
          <div ref={card1Ref} data-index={0} className={`lg:col-start-2 lg:col-span-5 group transition-all duration-1000 ease-out ${card1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ml-0 lg:ml-0`}>
            <CardItem item={domains[0]} />
          </div>

          {}
          <div className="hidden lg:block lg:col-start-7 lg:col-span-1 absolute left-1/2 top-12 bottom-12 w-px bg-border/60 pointer-events-none" style={{
          left: "50%"
        }} aria-hidden="true" />

          {}
          <div ref={quoteRef} className={`hidden lg:flex lg:col-start-7 lg:col-span-6 lg:row-start-1 items-start justify-end pt-4 pr-4 transition-all duration-1000 ease-out delay-300 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default italic text-xl xl:text-2xl leading-snug text-secondary max-w-xs text-right">
              — secured by design, verified by trust
            </p>
          </div>

          {}
          <div ref={card2Ref} data-index={1} className={`lg:col-start-7 lg:col-span-5 group lg:mt-[120px] ml-6 lg:ml-0 transition-all duration-1000 ease-out delay-150 ${card2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <CardItem item={domains[1]} />
          </div>
        </div>
      </div>
    </section>;
}
function CardItem({
  item
}: {
  item: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    aspect: string;
    to: string;
  };
}) {
  return <Link to={item.to} className="block transition-transform duration-700 ease-out hover:-translate-y-1.5">
      <div className={`relative w-full ${item.aspect} overflow-hidden rounded-lg bg-muted shadow-md`}>
        <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.03]" />
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <span className="font-default text-2xl text-primary leading-none">
          {item.number}
        </span>
        <span className="font-default text-xs tracking-[0.25em] uppercase text-muted-foreground">
          {item.label}
        </span>
        <h3 className="font-default text-3xl md:text-4xl leading-tight tracking-tight text-foreground">
          {item.title}
        </h3>
        <p className="font-default text-base leading-relaxed text-muted-foreground max-w-md mt-1">
          {item.description}
        </p>
        <Button variant="link" asChild className="mt-2 px-0 h-auto self-start text-foreground hover:text-primary group/btn">
          <span className="inline-flex items-center gap-2 font-default text-xs tracking-[0.2em] uppercase">
            View our protocols
            <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover/btn:translate-x-1.5" />
          </span>
        </Button>
      </div>
    </Link>;
}